import { Component, EventEmitter, OnInit, Output, ViewChild, inject } from '@angular/core';
import { NgModel } from '@angular/forms';

import { BytelTagService } from '@base/services/bytel-tag.service';
import { ViewService } from '@base/services/view.service';

import { JsonProduct } from '@catalog/products/interface/context';
import { PartnerProduct } from '@catalog/products/interface/phone';

import { RenewContextService } from '@context/child/renew-context.service';

import { MobileRecoveryService } from '@app/modules/mobile-recovery/mobile-recovery.service';
import {
    DevisRepriseMobileIn,
    DevisRepriseMobileOut,
    RecoveryStatus,
} from '@app/modules/mobile-recovery/modal-recovery/interface/modal-recovery-model';

import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'btl-modal-recovery-result',
    templateUrl: './modal-recovery-result.component.html',
    styleUrls: ['./modal-recovery-result.component.scss'],
})
export class ModalRecoveryResultComponent implements OnInit {
    @ViewChild('iban') ibanInputElement: NgModel;

    @Output() goToNextStep = new EventEmitter<void>();
    @Output() goToPreviousStep = new EventEmitter<void>();
    @Output() goToSearchStep = new EventEmitter<void>();
    public selectedPhone: PartnerProduct;
    public devis: DevisRepriseMobileOut;
    public devisIn: DevisRepriseMobileIn;
    public checkCondition: boolean = false;
    public showIban: boolean = false;
    private _scavenger = new Subject();
    public product: JsonProduct;
    public bonus: number = 0;
    public montantEstimeReprise: number = 0;
    public montantInsurance: number = 5;
    public montantTotal: number = 0;
    public ibanOrigin: string;
    public ibanValue: string;
    public isRenewal: boolean = false;
    public isWithoutTax: boolean;
    public MONTANT_MINIMUM_REPRISE = 10;
    public isLoading: boolean = true;

    private mobileRecoveryService: MobileRecoveryService = inject(MobileRecoveryService);
    private tagService: BytelTagService = inject(BytelTagService);
    private renewContextService: RenewContextService = inject(RenewContextService);
    private viewService: ViewService = inject(ViewService);

    public ngOnInit(): void {
        this.isRenewal = this.mobileRecoveryService.isRecoveryRenewal();
        this.selectedPhone = this.mobileRecoveryService.getItem('recoverySelectedPhone');
        this.devis = this.mobileRecoveryService.getDevis(this.selectedPhone.id);
        this.bonus = this.mobileRecoveryService.getBonus();

        if (this.isRenewal) {
            this.ibanOrigin = this.renewContextService.iban;
            this.ibanValue =
                this.mobileRecoveryService.hasIbanStorage() &&
                this.mobileRecoveryService.getIbanStorage() !== this.ibanOrigin
                    ? this.mobileRecoveryService.getIbanStorage()
                    : null;
            this.showIban = !!this.ibanValue;

            if (this.ibanOrigin) {
                const size = this.ibanOrigin.length - 5;
                this.ibanOrigin = this.ibanOrigin.substring(0, 4).padEnd(size, '*') + this.ibanOrigin.substring(size);
                this.ibanOrigin = this.ibanOrigin.replace(/(.{4})(?=.)/g, '$1 ');
            }
        }

        this.isWithoutTax = this.viewService.canApplyWithoutTaxSignal();

        this.mobileRecoveryService
            .getProduitsPartenaireDevis(this.selectedPhone.id, this.devis)
            .pipe(takeUntil(this._scavenger))
            .subscribe((response: DevisRepriseMobileIn) => {
                this.mobileRecoveryService.initStorageResult(this.devis, response, this.selectedPhone.id);
                this.devisIn = response;
                this.montantInsurance = this.devisIn.livraison[0].montantAssurance;
                this.montantEstimeReprise = this.devisIn.montantEstimeReprise;
                this.callTotalPrice();

                if (this.montantEstimeReprise < this.MONTANT_MINIMUM_REPRISE) {
                    this.mobileRecoveryService.setRecoveryStatus(RecoveryStatus.UNSTARTED);
                    this.mobileRecoveryService.removeItem(this.mobileRecoveryService.MOBILE_RECOVERY_STEP_STORAGE_KEY);
                }
                this.isLoading = false;
            });

        this.sendTag();
    }

    public ngOnDestroy(): void {
        // Used for scavenger observable unsubscriptions
        this._scavenger.next(null);
        this._scavenger.complete();
    }

    public close(force: boolean = false): void {
        if (this.canShowButton() || force) {
            let ibanToStorage = '';
            if (this.isRenewal) {
                ibanToStorage = this.ibanValue && this.showIban ? this.ibanValue : this.renewContextService.iban;
            }
            this.mobileRecoveryService.setIban(ibanToStorage);
            this.mobileRecoveryService.setRecoveryStatus(RecoveryStatus.COMPLETED);
            this.mobileRecoveryService.clearRecoveryStorage();
            this.mobileRecoveryService.phoneSubject.next(null);

            this.goToNextStep.emit();

            if (this.mobileRecoveryService.getAmountSaved() > 0) {
                this.mobileRecoveryService.saveRecoveryInfos();
            }

            this.tagService.send({
                nomClic: 'Finalisation_reprise_mobile_formulaire_e3',
                categorieClic: 'Reprise_mobile',
                actionClic: 'Clic_selection',
            });
        }
    }

    public callTotalPrice(): void {
        this.montantTotal = this.montantEstimeReprise + this.bonus;
    }

    public useNewIban(event): void {
        this.showIban = event === 'Oui' ? false : true;
    }

    public updateCondition(): void {
        this.checkCondition = !this.checkCondition;
    }

    public previous(): void {
        this.goToPreviousStep.emit();
    }

    public goToSearch(): void {
        this.goToSearchStep.emit();
    }

    public canShowButton(): boolean {
        let ibanValid = true;

        if (this.showIban) {
            ibanValid = this.ibanInputElement?.control?.valid && !!this.ibanValue;
        }

        return this.checkCondition && ibanValid;
    }

    public sendTag(): void {
        this.tagService.send({
            page: 'E3_Infos_assurance_mobile',
            pageCategorie: 'Formulaire',
        });
    }
}
